import React, { useState } from 'react';
import { connect } from 'react-redux';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { useConfiguration } from '../../context/configurationContext';
import { LayoutComposer, Page, PageHero } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import MirageCalculator from './MirageCalculator';
import SunPathCalculator from './SunPathCalculator';
import UPTCalculator from './UPTCalculator';
import AerodyneCalculator from './AerodyneCalculator';

import css from './HarnessSizingGuidePage.module.css';

const IMG = '/static/images/harness-guide';

// ---------- Accordion item ----------

const AccordionItem = ({ id, title, number, open, onToggle, children }) => {
  return (
    <div className={css.accordionCard} id={id}>
      <button
        type="button"
        className={css.accordionTrigger}
        onClick={() => onToggle(id)}
        aria-expanded={open}
        aria-controls={`panel-${id}`}
        id={`trigger-${id}`}
      >
        {number && <span className={css.accordionNumber}>{number}</span>}
        <h3 className={css.accordionTitle}>{title}</h3>
        <span className={`${css.chevron} ${open ? css.chevronOpen : ''}`} />
      </button>
      <div
        className={`${css.accordionPanelWrap} ${open ? css.accordionPanelOpen : ''}`}
        id={`panel-${id}`}
        role="region"
        aria-labelledby={`trigger-${id}`}
      >
        <div className={css.accordionPanel}>
          {children}
        </div>
      </div>
    </div>
  );
};

// ---------- Page schema (built per-render so we can use the absolute root URL) ----------

function buildPageSchema(rootURL) {
  return {
    '@type': 'HowTo',
    name: 'Harness Sizing Guide for Skydiving Rigs',
    url: `${rootURL}/harness-sizing`,
    description:
      'Comprehensive guide on how to measure and check if a used or new skydiving rig fits you, with detailed instructions for UPT, Sun Path, Aerodyne, Mirage, Velocity, and Wings harnesses.',
    image: `${rootURL}${IMG}/hero-desktop.webp`,
    totalTime: 'PT30M',
    tool: [
      { '@type': 'HowToTool', name: 'Flexible measuring tape (centimetres and inches)' },
      { '@type': 'HowToTool', name: "Rig data card or TSO label" },
      { '@type': 'HowToTool', name: "Access to the rig's manufacturer sizing chart or calculator" },
    ],
    supply: [
      { '@type': 'HowToSupply', name: 'Rig serial number' },
      { '@type': 'HowToSupply', name: 'Body measurements (height, weight, yoke, MLW, laterals)' },
    ],
    step: [
      {
        '@type': 'HowToStep',
        name: 'Understand Parts of a Rig',
        text:
          'Learn the basic components of a harness that are critical for fit: Yoke, Main Lift Web (MLW), Laterals/Stabilizers, and Leg Pads.',
      },
      {
        '@type': 'HowToStep',
        name: 'Take Your Measurements',
        text:
          "Measure yourself according to the specific manufacturer's instructions. Each manufacturer has different measurement requirements.",
      },
      {
        '@type': 'HowToStep',
        name: 'Locate the TSO/Data Card',
        text:
          "Find the rig's data card or TSO label which contains harness measurements and serial number information.",
      },
      {
        '@type': 'HowToStep',
        name: 'Compare Measurements',
        text:
          "Compare your measurements with the rig's specifications. For Yoke: +/-1 inch is usually fine. For MLW: +/-1 inch fits well. For Laterals: +/-0.5 inch will fit.",
      },
      {
        '@type': 'HowToStep',
        name: 'Contact Manufacturer if Needed',
        text:
          'Reach out to the manufacturer with your measurements and the serial number to confirm fit.',
      },
    ],
    mentions: [
      { '@type': 'Organization', name: 'UPT Vector' },
      { '@type': 'Organization', name: 'Sun Path Products (Javelin)' },
      { '@type': 'Organization', name: 'Aerodyne Research (Icon)' },
      { '@type': 'Organization', name: 'Mirage Systems' },
      { '@type': 'Organization', name: 'Velocity Sports Equipment' },
      { '@type': 'Organization', name: 'Wings (SunRise Rigging)' },
    ],
    about: {
      '@type': 'Thing',
      name: 'Skydiving harness fit and sizing',
    },
    audience: {
      '@type': 'Audience',
      audienceType: 'Skydivers buying a used rig',
    },
    inLanguage: 'en',
  };
}

// ---------- Main page ----------

const layoutAreas = `
  topbar
  main
  footer
`;

const SECTION_IDS = ['upt', 'sunpath', 'aerodyne', 'mirage', 'velocity', 'wings'];

const HarnessSizingGuidePageComponent = props => {
  const { scrollingDisabled } = props;
  const config = useConfiguration();

  // Accordion state lifted so nav can open sections directly
  const [openSections, setOpenSections] = useState(() => {
    if (typeof window !== 'undefined' && window.location.hash) {
      const hash = window.location.hash.slice(1);
      if (SECTION_IDS.includes(hash)) return { [hash]: true };
    }
    return {};
  });

  const toggleSection = id => {
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const openAndScrollTo = id => {
    setOpenSections(prev => ({ ...prev, [id]: true }));
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const schemaTitle = 'Skydiving Harness Sizing Guide & Calculator | Avento';
  const schemaDescription =
    'How to measure harness fit for a used skydiving rig. Calculator & measurement guide for UPT, Sun Path, Aerodyne, Mirage, Velocity and Wings.';

  const rootURL = config.marketplaceRootURL || '';
  const pageSchema = buildPageSchema(rootURL);
  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${rootURL}/` },
      { '@type': 'ListItem', position: 2, name: 'Harness Sizing Guide', item: `${rootURL}/harness-sizing` },
    ],
  };

  return (
    <Page
      title={schemaTitle}
      description={schemaDescription}
      schema={[pageSchema, breadcrumbSchema]}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutComposer areas={layoutAreas} className={css.layout}>
        {layoutProps => {
          const { Topbar, Main, Footer } = layoutProps;
          return (
            <>
              <Topbar as="header" className={css.topbar}>
                <TopbarContainer currentPage="HarnessSizingGuidePage" />
              </Topbar>
              <Main as="main" className={css.main}>
      {/* Hero */}
      <PageHero
        title="Harness Sizing Guide"
        narrowTitle
        subtitle="Everything you need to know to figure out if a used rig fits you, with sizing calculators for the most popular manufacturers"
        desktopImage={`${IMG}/hero-desktop.webp`}
        desktopWidth={3024}
        desktopHeight={700}
        mobileImage={`${IMG}/hero-mobile.webp`}
        mobileWidth={780}
        mobileHeight={571}
      />

      {/* Introduction */}
      <section className={css.section}>
        <div className={css.container}>
          <h2 className={css.sectionHeading}>Introduction</h2>
          <p className={css.paragraph}>
            When you buy a new rig, the harness is custom made for your measurements. If you
            buy used, determining harness fit is one of the most important factors to make sure
            you have a rig that fits you and is fun to jump. Weight and size are only indicators
            and not enough to determine fit.
          </p>
          <p className={css.paragraph}>
            To reliably assess harness fit, you will need your measurements according to the
            manufacturer's instructions and the harness measurements of the rig you are looking
            to buy. Many manufacturers show (parts of) these measurements on the TSO label.
            Further, the manufacturers have the information of which rig has which measurements,
            so you can reach out if you know the serial number.
          </p>
          <p className={css.paragraph}>
            This guide is a comprehensive summary of information about harness sizing of
            different manufacturers. It is based on thorough research and information that we
            received from manufacturers directly. For most major manufacturers, we built
            calculators approximating harness fit.
          </p>
        </div>
      </section>

      {/* Horizontal section navigation */}
      <div className={css.sectionNavWrapper}>
      <nav className={css.sectionNav} aria-label="Manufacturers">
        {[
          { id: 'upt', num: '01', label: 'UPT' },
          { id: 'sunpath', num: '02', label: 'Sun Path' },
          { id: 'aerodyne', num: '03', label: 'Aerodyne' },
          { id: 'mirage', num: '04', label: 'Mirage' },
          { id: 'velocity', num: '05', label: 'Velocity' },
          { id: 'wings', num: '06', label: 'Wings' },
        ].map(item => (
          <a
            key={item.id}
            href={`#${item.id}`}
            className={css.navItem}
            onClick={e => {
              e.preventDefault();
              openAndScrollTo(item.id);
            }}
          >
            <span className={css.navNumber}>{item.num}</span> {item.label}
          </a>
        ))}
      </nav>
      </div>

      {/* Parts of a Rig */}
      <section className={css.section} id="parts-of-rig">
        <div className={css.container}>
          <h2 className={css.sectionHeading}>Parts of a Rig</h2>
          <p className={css.paragraph}>
            To understand the measurements of the harness, it helps to know some basic
            components of a harness that are critical for fit.
          </p>
          <img
            src={`${IMG}/rig-parts.webp`}
            alt="A visualisation of the different parts of a skydiving rig"
            className={css.rigPartsImage}
            width={729}
            height={600}
            loading="lazy"
          />

          <h3 className={css.subHeading}>Yoke</h3>
          <ul className={css.bulletList}>
            <li className={css.bulletItem}>
              The U-shaped section of the harness which runs from the back of your rig, over
              your shoulders, to your chest. This part of the harness can't be adjusted.
            </li>
            <li className={css.bulletItem}>
              A yoke in the range of +/- 1 inch (2.54 cm) is fine, especially for larger sizes.
            </li>
          </ul>

          <h3 className={css.subHeading}>Main Lift Web (MLW)</h3>
          <ul className={css.bulletList}>
            <li className={css.bulletItem}>
              The strap that runs down the side of the torso. It is important for harness fit
              but can also be adjusted easily by a master rigger <strong>if</strong> the rig has
              chest rings. Chest rings are metal rings where the chest-strap and MLW connect.
            </li>
            <li className={css.bulletItem}>
              Everything in the range of +/- 1 inch (2.54 cm) fits well and will not need any
              adjusting.
            </li>
          </ul>

          <h3 className={css.subHeading}>Laterals / Stabilizers</h3>
          <ul className={css.bulletList}>
            <li className={css.bulletItem}>
              The part of the harness that wraps around the waist, connecting the container to
              the main lift web. They can usually be adjusted relatively easy by a master
              rigger.
            </li>
            <li className={css.bulletItem}>
              Everything in the range of +/- 0.5 inch (1.27 cm) will fit and does not need to
              be adjusted. If it is a bit outside that range, a belly band can help, but is no
              perfect solution.
            </li>
          </ul>

          <h3 className={css.subHeading}>Leg Pads</h3>
          <ul className={css.bulletList}>
            <li className={css.bulletItem}>
              The leg pads are the part of the harness that goes around your leg. They are also
              inexpensive to be adjusted by a master rigger.
            </li>
            <li className={css.bulletItem}>
              Furthermore, they have a pretty large range where the fit is still good. This
              will most likely not be the problem.
            </li>
          </ul>
        </div>
      </section>

      {/* Manufacturers */}
      <section className={css.section} id="manufacturers">
        <div className={css.container}>
          <h2 className={css.sectionHeading}>Manufacturers</h2>
          <div className={css.accordion}>
            {/* ----- UPT ----- */}
            <AccordionItem id="upt" title="UPT" number="01" open={!!openSections.upt} onToggle={toggleSection}>
              <p className={css.panelText}>
                UPT shows MLW and Yoke sizes on the TSO label (the + or - after the MLW
                number). Keep in mind that UPT rigs generally don't have chest rings which would
                make adjusting the MLW expensive.
              </p>
              <p className={css.panelText}>
                You can see tips from UPT themselves about buying a used Vector in{' '}
                <a
                  href="https://youtu.be/R6YZcjUjrak?si=LkBJOfS-V2I4eAWD"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  this video
                </a>
                .
              </p>

              <h4 className={css.panelSubHeading}>TSO Label</h4>
              <img
                src={`${IMG}/upt-data-card.webp`}
                alt="Data Card of UPT Skydiving Rig"
                className={css.dataCardImage}
                width={600}
                height={400}
                loading="lazy"
              />
              <p className={css.panelText}>
                On UPT's TSO label you can find the serial number as well as MLW (here 18.5)
                and Yoke adjustment (here +2).
              </p>

              <h4 className={css.panelSubHeadingLarge}>Take Measurements</h4>
              <p className={css.panelText}>
                Check out{' '}
                <a
                  href="https://www.youtube.com/watch?v=TQfp9VzveN4"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  this
                </a>
                {' '}video for instructions on taking your measurements. You will need this for
                determining harness fit and it is important to do this precisely!
              </p>

              <h4 className={css.panelSubHeadingLarge}>UPT Harness Calculator</h4>
              <p className={css.panelText}>
                Use this calculator to determine which UPT harness size you would need. The
                formula is based on information that UPT shared with us at the European
                Skydiving Symposium 26. It is how UPT does it for used rigs as well and works
                well for heights between 165-188cm.
              </p>
              <UPTCalculator />

              <div className={css.interpretation}>
                <p className={css.panelText}>
                  <strong>Interpretation</strong>
                </p>
                <ul className={css.panelList}>
                  <li className={css.panelListItem}>
                    <strong>Yoke:</strong> If the yoke of the rig you are looking to buy is +/-
                    1 from your size it should fit fine (especially for larger sizes), but best
                    if it fits exactly
                  </li>
                  <li className={css.panelListItem}>
                    <strong>MLW:</strong> If the MLW of the rig you are looking to buy is +/- 1
                    from your size it should fit fine. Also if the deal is good enough, it might
                    even make sense to get it changed by a master rigger.
                  </li>
                </ul>
              </div>

              <h4 className={css.panelSubHeading}>Contact UPT</h4>
              <p className={css.panelText}>
                Note that the calculator functions as a good estimate but is no guarantee for
                perfect fit as harness sizing depends on many factors such as if you have a
                special body shape.
              </p>
              <p className={css.panelText}>
                We always recommend to contact UPT directly. You will need the Serial Number
                (Always Visible on Avento Listings) and your careful measurements according to
                UPT's instructions.
              </p>
              <p className={css.panelText}>
                With this information you can contact Mark from UPT
                under{' '}
                <a href="mailto:markk@uptvector.com">markk@uptvector.com</a>
              </p>
            </AccordionItem>

            {/* ----- Sun Path ----- */}
            <AccordionItem id="sunpath" title="Sun Path" number="02" open={!!openSections.sunpath} onToggle={toggleSection}>
              <p className={css.panelText}>
                Sun Path shows the Yoke and MLW size on the TSO label. Furthermore, we have
                made positive experience with their customer support team.
              </p>

              <h4 className={css.panelSubHeading}>TSO Label</h4>
              <img
                src={`${IMG}/sunpath-data-card.webp`}
                alt="Data Card of Sun Path Skydiving Rig"
                className={css.dataCardImage}
                width={600}
                height={400}
                loading="lazy"
              />
              <ul className={css.panelList}>
                <li className={css.panelListItem}>
                  C (from "C-18") is the yoke size. Yoke sizes are (from smallest to largest)
                  O/A/B/C/D. Women usually have size A/B and men C/D.
                </li>
                <li className={css.panelListItem}>
                  18 (from "C-18") is the MLW
                </li>
              </ul>
              <p className={css.panelText}>
                On the TSO label you can find the serial number which you can use to contact
                Sun Path with your measurements.
              </p>

              <h4 className={css.panelSubHeadingLarge}>Take Measurements</h4>
              <p className={css.panelText}>
                You can find instructions on how to take your measurements according to Sun
                Path{' '}
                <a
                  href="https://youtu.be/oAXrX89SJgQ?si=bP6nsof_r621onSM"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </p>

              <h4 className={css.panelSubHeadingLarge}>Sun Path Harness Calculator</h4>
              <p className={css.panelText}>
                To calculate your harness size, use this calculator. Make all measurements
                according to Sun Path's measuring guide (linked above). The calculations are
                based on a seminar by Tom Parker & Rob Kendall from Sun Path which you can
                watch{' '}
                <a
                  href="https://www.youtube.com/watch?v=B7vEOXOK-GA&t=990s"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </p>
              <p className={css.panelText}>
                <strong>Important</strong>: Harness sizing depends on many factors, including
                professional experience. This calculator gives you an approximate overview. We
                always recommend contacting the manufacturer with your measurements and the
                rig's serial number, especially as Sun Path responds quickly.
              </p>
              <SunPathCalculator />

              <div className={css.interpretation}>
                <p className={css.panelText}>
                  <strong>Interpretation</strong>
                </p>
                <ul className={css.panelList}>
                  <li className={css.panelListItem}>
                    <strong>Yoke:</strong> If the yoke of the rig you are looking to buy is +/-
                    1 from your size it should fit fine (especially for larger sizes), but best
                    if it fits exactly
                  </li>
                  <li className={css.panelListItem}>
                    <strong>MLW:</strong> If the MLW of the rig you are looking to buy is +/- 1
                    from your size it should fit fine. Also if the deal is good enough, it might
                    even make sense to get it changed by a master rigger.
                  </li>
                </ul>
              </div>

              <h4 className={css.panelSubHeading}>Contact Sun Path</h4>
              <p className={css.panelText}>
                As the calculator is not an exact guarantee for perfect fit, we always recommend
                to contact Sun Path directly. You will need:
              </p>
              <ol className={css.panelNumberedList}>
                <li className={css.panelListItem}>
                  The information on the rig's TSO label (picture above)
                </li>
                <li className={css.panelListItem}>
                  Your measurements according to Sun Path's instructions
                </li>
              </ol>
              <p className={css.panelText}>
                If you have this, you can contact them with your measurements and the rig's
                serial number:
              </p>
              <ul className={css.panelList}>
                <li className={css.panelListItem}>
                  <a href="mailto:info@sunpath.com">info@sunpath.com</a>
                </li>
              </ul>
              <p className={css.panelText}>
                They usually respond within 24 hours during the week and on Monday morning for
                inquiries on weekends.
              </p>
            </AccordionItem>

            {/* ----- Aerodyne ----- */}
            <AccordionItem id="aerodyne" title="Aerodyne" number="03" open={!!openSections.aerodyne} onToggle={toggleSection}>
              <p className={css.panelText}>
                Aerodyne shows harness size on the TSO label with letters from A to J++ (A
                being the smallest). These letters correspond to different harness dimensions
                such as the Main Lift Web, chest strap, waist/lateral, and leg strap.
              </p>
              <p className={css.panelText}>
                We found the Aerodyne ICON Harness Sizing Chart (shown below), which was
                shared by a rigger on Reddit and shows the sizing logic used for ICON rigs.
                Based on this chart, we built a calculator below to help you estimate harness
                fit.
              </p>
              <img
                src={`${IMG}/aerodyne-sizing-chart.webp`}
                alt="Aerodyne ICON Harness Sizing Chart showing letter-based sizing for MLW, chest strap, stabilizer, and leg strap"
                className={css.dataCardImage}
                width={600}
                height={400}
                loading="lazy"
              />

              <h4 className={css.panelSubHeading}>TSO Label</h4>
              <img
                src={`${IMG}/aerodyne-data-card.webp`}
                alt="Data Card of Aerodyne Skydiving Rig"
                className={css.dataCardImage}
                width={600}
                height={400}
                loading="lazy"
              />
              <p className={css.panelText}>
                On Aerodyne's TSO label, you can see the harness size (here "C-B-B-E"). The
                measurements stand for "MLW-cheststrap-stabilizer-legstrap".
              </p>

              <h4 className={css.panelSubHeadingLarge}>Take Measurements</h4>
              <p className={css.panelText}>
                You can find Aerodyne's instructions on how to take your measurements{' '}
                <a
                  href="https://www.youtube.com/watch?v=X21J3AMFISY"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </p>

              <h4 className={css.panelSubHeadingLarge}>Aerodyne Harness Calculator</h4>
              <p className={css.panelText}>
                <strong>Important</strong>: Harness sizing depends on many factors, including
                professional experience. This calculator gives you an approximate overview. We
                always recommend contacting the manufacturer with your measurements and the
                rig's serial number.
              </p>
              <AerodyneCalculator />

              <div className={css.interpretation}>
                <p className={css.panelText}>
                  <strong>Interpretation</strong>
                </p>
                <ul className={css.panelList}>
                  <li className={css.panelListItem}>
                    MLW is the most important metric. Best if it fits exactly but if it is
                    within 1 range it should also fit fine.
                  </li>
                  <li className={css.panelListItem}>
                    If the Leg Pad measurement does not fit it is usually not a problem as they
                    are adjustable anyways.
                  </li>
                </ul>
              </div>

              <h4 className={css.panelSubHeading}>Contact Aerodyne</h4>
              <p className={css.panelText}>
                For more information and more accurate judgement you can contact Aerodyne with
                the serial number of the rig you are looking to buy and your measurements
                at{' '}
                <a
                  href="mailto:info@flyaerodyne.com?subject=Inquiry%20-%20Used%20Aerodyne%20Harness%20Size"
                >
                  info@flyaerodyne.com
                </a>
                .
              </p>
            </AccordionItem>

            {/* ----- Mirage ----- */}
            <AccordionItem id="mirage" title="Mirage" number="04" open={!!openSections.mirage} onToggle={toggleSection}>
              <p className={css.panelText}>
                Mirage has the measurements of the rig's harness written on its TSO Label,
                which is very helpful. As measuring is quite complex and individual, we always
                recommend to contact Mirage directly as they have great customer support and
                are responding quickly.
              </p>
              <p className={css.panelText}>
                You can find Mirage's own guide{' '}
                <a
                  href="https://miragesys.com/support/faq-buying-a-used-mirage/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </p>

              <h4 className={css.panelSubHeadingLarge}>Take Measurements</h4>
              <p className={css.panelText}>
                You can find the instructions to take your measurements according to Mirage{' '}
                <a
                  href="https://miragesys.com/support/harness-sizing-calculator/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  here
                </a>
                .
              </p>

              <h4 className={css.panelSubHeading}>TSO Label</h4>
              <img
                src={`${IMG}/mirage-data-card.webp`}
                alt="Data Card of Mirage Skydiving Rig"
                className={css.dataCardImage}
                width={600}
                height={400}
                loading="lazy"
              />
              <h4 className={css.panelSubHeading}>Figure out if the Harness fits</h4>
              <p className={css.panelText}>
                To figure out if the harness of the rig you are looking to buy fits, you need 2
                things:
              </p>
              <ol className={css.panelNumberedList}>
                <li className={css.panelListItem}>
                  The information on the rig's TSO Label (picture above)
                </li>
                <li className={css.panelListItem}>
                  Your measurements according to Mirage's instructions
                </li>
              </ol>
              <p className={css.panelText}>
                If you have this, you can contact them with your measurements and the rig's
                serial number:
              </p>
              <ul className={css.panelList}>
                <li className={css.panelListItem}>
                  <a href="mailto:sales@miragesys.com">sales@miragesys.com</a>
                </li>
                <li className={css.panelListItem}>(386) 740-9222</li>
              </ul>

              <h4 className={css.panelSubHeadingLarge}>Mirage Harness Calculator</h4>
              <p className={css.panelText}>
                To calculate your harness size, use this calculator. Make all measurements
                according to Mirage's measuring guide (linked above). The calculations are
                based on the size references on the Mirage website.
              </p>
              <p className={css.panelText}>
                <strong>Important</strong>: Harness sizing depends on many factors, including
                professional experience. This calculator gives you an approximate overview. We
                always recommend contacting the manufacturer with your measurements and the
                rig's serial number, especially as Mirage responds quickly.
              </p>
              <MirageCalculator />

              <div className={css.yokeAdjustments}>
                <p className={css.panelText}>
                  <strong>Common Yoke adjustments (Optional)</strong>
                </p>
                <ul className={css.panelList}>
                  <li className={css.panelListItem}>Male in short series: +1</li>
                  <li className={css.panelListItem}>
                    Female A-B cup in standard series: -1
                  </li>
                  <li className={css.panelListItem}>Female C cup in M5-M8: -1</li>
                  <li className={css.panelListItem}>Female D cup in short series: +1</li>
                  <li className={css.panelListItem}>
                    Male with thick shoulders in MX thru M4: +1
                  </li>
                </ul>
              </div>

              <div className={css.interpretation}>
                <p className={css.panelText}>
                  <strong>Interpretation</strong>
                </p>
                <ul className={css.panelList}>
                  <li className={css.panelListItem}>
                    A <strong>yoke</strong> in the range of +/-1 of your recommended size is
                    usually fine (especially for larger sizes)
                  </li>
                  <li className={css.panelListItem}>
                    An <strong>MLW</strong> size in the range of +/-1 of your recommended size
                    is usually fine (and can be adjusted by a master rigger cheaply if the rig
                    has chest rings)
                  </li>
                  <li className={css.panelListItem}>
                    A <strong>stabilizer</strong> size range of +/- 0.5 of your recommended
                    size will fit and does not need to be adjusted. If it is a bit outside that
                    range, a belly band can help, but is no perfect solution. (This can also be
                    adjusted by a master rigger easily).
                  </li>
                  <li className={css.panelListItem}>
                    A <strong>leg pad</strong> size range within a couple inches of your
                    recommended size is usually fine and does not need to be adjusted. If it
                    needs to be adjusted, this can also be done easily.
                  </li>
                </ul>
              </div>
            </AccordionItem>

            {/* ----- Velocity ----- */}
            <AccordionItem id="velocity" title="Velocity" number="05" open={!!openSections.velocity} onToggle={toggleSection}>
              <p className={css.panelText}>
                Velocity Sports Equipment (Infinity rigs) offers a great tool for finding out
                your harness size: a rig lookup by serial number that gives you the exact
                harness measurements.
              </p>

              <h4 className={css.panelSubHeading}>Rig Lookup by Serial Number</h4>
              <p className={css.panelText}>
                Velocity has a support tool on their website where you can enter the serial
                number of the rig you are looking to buy and get the exact harness
                measurements. This makes it very easy to determine fit.
              </p>
              <p className={css.panelText}>
                <a
                  href="https://www.velocityrigs.com/support/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Use the Velocity Rig Lookup Tool
                </a>
              </p>

              <h4 className={css.panelSubHeadingLarge}>Take Measurements</h4>
              <p className={css.panelText}>
                Velocity also has a measuring guide on their website. You can find it on the{' '}
                <a
                  href="https://www.velocityrigs.com/support/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Velocity support page
                </a>
                .
              </p>

              <h4 className={css.panelSubHeading}>TSO Label</h4>
              <img
                src={`${IMG}/velocity-data-card.webp`}
                alt="TSO Label of Velocity Infinity Skydiving Rig"
                className={css.dataCardImage}
                width={600}
                height={450}
                loading="lazy"
              />
              <p className={css.panelText}>
                Below is an example of a Velocity Infinity TSO label. The serial number
                visible on the label can be used with the lookup tool above to get the exact
                harness measurements.
              </p>

              <h4 className={css.panelSubHeading}>Figure out if the Harness fits</h4>
              <p className={css.panelText}>
                Use the serial number lookup tool to get the rig's exact measurements, then
                compare them with your own measurements. If you need further assistance, you
                can reach Velocity through their support page.
              </p>
            </AccordionItem>

            {/* ----- Wings ----- */}
            <AccordionItem id="wings" title="Wings" number="06" open={!!openSections.wings} onToggle={toggleSection}>
              <p className={css.panelText}>
                Although Wings does not show the exact measurements of the harness on the TSO
                label, we have made positive experience with their customer support team.
              </p>

              <h4 className={css.panelSubHeadingLarge}>Take Measurements</h4>
              <p className={css.panelText}>
                We could not find a public measuring guide from Wings. You can use the one from
                another manufacturer, e.g.{' '}
                <a
                  href="https://youtu.be/TQfp9VzveN4?si=nymLggWsqQT1kkeP"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  UPT
                </a>
                . Just make sure to inform them about which one you used if you contact them.
              </p>

              <h4 className={css.panelSubHeading}>TSO Label</h4>
              <img
                src={`${IMG}/wings-data-card.webp`}
                alt="Data Card of Wings Skydiving Rig"
                className={css.dataCardImage}
                width={600}
                height={400}
                loading="lazy"
              />
              <p className={css.panelText}>
                On the TSO label you can find the serial number which you can use to contact
                Wings with your measurements.
              </p>

              <h4 className={css.panelSubHeading}>Figure out if the Harness fits</h4>
              <p className={css.panelText}>
                To figure out if the harness fits, you need 2 things:
              </p>
              <ol className={css.panelNumberedList}>
                <li className={css.panelListItem}>
                  The information on the rig's TSO label (picture above)
                </li>
                <li className={css.panelListItem}>Your measurements</li>
              </ol>
              <p className={css.panelText}>
                If you have this, you can <strong>contact</strong> them with your measurements
                and the rig's serial number:
              </p>
              <ul className={css.panelList}>
                <li className={css.panelListItem}>sales@skydivewings.com</li>
              </ul>
              <p className={css.panelText}>
                They respond quickly and provide you information on if the rig fits.
              </p>
            </AccordionItem>
          </div>
        </div>
      </section>

      {/* Exact Measurements */}
      <section className={`${css.section} ${css.lastSection}`} id="exact-measurements">
        <div className={css.container}>
          <h2 className={css.sectionHeading}>
            Exact Harness Measurements
          </h2>
          <p className={css.paragraph}>
            If you know the exact harness measurements (for example because the seller has
            stated the information on the listing), that is great! You can determine even
            better if the harness will fit you.
          </p>
          <p className={css.paragraph}>
            However, most manufacturers determine harness size differently. We still recommend
            contacting the manufacturer with your measurements to determine reliably if the
            harness will fit you. If that is not an option, try to contact your rigger so
            they can help you determine if the harness would fit you.
          </p>
        </div>
      </section>

              </Main>
              <Footer>
                <FooterContainer />
              </Footer>
            </>
          );
        }}
      </LayoutComposer>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const HarnessSizingGuidePage = connect(mapStateToProps)(HarnessSizingGuidePageComponent);

export default HarnessSizingGuidePage;
